// components/viz/TernaryDiagram.jsx
// 等温三元相图 SVG 组件

import { PHASE_COLORS } from '../../data/systems';

/* ════════════════════════════════════════════════════════════════════
   2D · Isothermal Ternary Diagram
   ════════════════════════════════════════════════════════════════════ */
const TRI_W = 580, TRI_H = 510;
const APEX  = { x: TRI_W / 2,    y: 50 };
const LEFT  = { x: 60,           y: TRI_H - 60 };
const RIGHT = { x: TRI_W - 60,   y: TRI_H - 60 };
const ternaryToXY = (w1, w2) => {
  const ww = 1 - w1 - w2;
  return {
    x: LEFT.x * ww + RIGHT.x * w1 + APEX.x * w2,
    y: LEFT.y * ww + RIGHT.y * w1 + APEX.y * w2,
  };
};

export default function TernaryDiagram({ dataPoints, predictions, invariantPoints, labels, showPrediction, showRegions, hoveredPoint, onHoverPoint, selectedPhase, phaseList, livePoint, onSelectPoint, selectedPointId, onSelectCurvePoint }) {
  const filteredPts = selectedPhase === "all" ? dataPoints : dataPoints.filter(p => p.solidPhase === selectedPhase);

  return (
    <svg viewBox={`0 0 ${TRI_W} ${TRI_H}`} style={{ width: "100%", height: "100%" }}>
      <defs>
        <linearGradient id="triFill" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#f0f9ff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#ecfeff" stopOpacity="0.4" />
        </linearGradient>
        <filter id="curveGlow"><feGaussianBlur stdDeviation="1.6" /></filter>
        <pattern id="dotgrid" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="#cbd5e1" opacity="0.4" />
        </pattern>
      </defs>

      {/* Background */}
      <rect width={TRI_W} height={TRI_H} fill="url(#dotgrid)" opacity="0.5" />

      {/* Triangle */}
      <polygon
        points={`${APEX.x},${APEX.y} ${LEFT.x},${LEFT.y} ${RIGHT.x},${RIGHT.y}`}
        fill="url(#triFill)" stroke="#0f172a" strokeWidth="1.8"
      />

      {/* Phase region tints */}
      {showRegions && predictions.length >= 2 && (
        <g opacity="0.10">
          {predictions.map((curve, ci) => {
            if (curve.length < 3) return null;
            const path = curve.map((p,i) => { const xy = ternaryToXY(p.w1,p.w2); return `${i===0?"M":"L"}${xy.x},${xy.y}`; }).join(" ");
            const corner = ci === 0 ? RIGHT : ci === 1 ? APEX : LEFT;
            return <path key={ci} d={`${path} L${corner.x},${corner.y} Z`} fill={PHASE_COLORS[ci]} />;
          })}
        </g>
      )}

      {/* Grid lines (parallel to each side) */}
      {[0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9].map(f => {
        const a = ternaryToXY(f, 0);
        const b = ternaryToXY(f, 1 - f);
        const c = ternaryToXY(0, f);
        const d = ternaryToXY(1 - f, f);
        const e = ternaryToXY(0, f);
        const g = ternaryToXY(1 - f, 0);
        return (
          <g key={f} stroke="#cbd5e1" strokeWidth="0.6" opacity={f === 0.5 ? 0.7 : 0.4}>
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
            <line x1={c.x} y1={c.y} x2={d.x} y2={d.y} />
            <line x1={e.x} y1={e.y} x2={g.x} y2={g.y} />
          </g>
        );
      })}

      {/* Tick labels on edges (LARGER) */}
      {[0,0.2,0.4,0.6,0.8,1.0].map(f => {
        const pb = ternaryToXY(f, 0);
        const pl = ternaryToXY(0, f);
        const pr = ternaryToXY(f, 1 - f);
        return (
          <g key={`tick-${f}`} fontFamily="'JetBrains Mono', monospace" fontSize="13" fontWeight="600" fill="#475569">
            <text x={pb.x} y={pb.y + 22} textAnchor="middle">{(f * 100).toFixed(0)}</text>
            <text x={pl.x - 12} y={pl.y + 5} textAnchor="end">{(f * 100).toFixed(0)}</text>
            <text x={pr.x + 12} y={pr.y + 5} textAnchor="start">{(f * 100).toFixed(0)}</text>
          </g>
        );
      })}

      {/* Apex labels (LARGER) */}
      <g fontFamily="'IBM Plex Sans', sans-serif" fontWeight="700">
        <text x={LEFT.x - 18} y={LEFT.y + 32} textAnchor="middle" fontSize="18" fill="#0f172a">{labels[2]}</text>
        <text x={RIGHT.x + 18} y={RIGHT.y + 32} textAnchor="middle" fontSize="18" fill="#0f172a">{labels[0]}</text>
        <text x={APEX.x} y={APEX.y - 22} textAnchor="middle" fontSize="18" fill="#0f172a">{labels[1]}</text>
        {/* corner symbols */}
        <circle cx={LEFT.x} cy={LEFT.y} r="4" fill="#0f172a" />
        <circle cx={RIGHT.x} cy={RIGHT.y} r="4" fill="#0f172a" />
        <circle cx={APEX.x} cy={APEX.y} r="4" fill="#0f172a" />
      </g>

      {/* axis baseline label */}
      <text x={TRI_W/2} y={TRI_H - 18} textAnchor="middle" fontSize="13" fill="#64748b" fontWeight="500" fontFamily="'IBM Plex Sans',sans-serif">w 质量分率 (%)</text>

      {/* Prediction curves — clickable + sample dots on the curve */}
      {showPrediction && predictions.map((curve, ci) => {
        const phaseLabel = phaseList[ci] || `相 ${ci+1}`;
        const col = PHASE_COLORS[ci % PHASE_COLORS.length];
        return (
          <g key={`curve-${ci}`}>
            {/* Glow halo */}
            <polyline
              points={curve.map(p => { const xy = ternaryToXY(p.w1, p.w2); return `${xy.x},${xy.y}`; }).join(" ")}
              fill="none" stroke={col} strokeWidth="3.2" strokeLinejoin="round"
              opacity="0.85" filter="url(#curveGlow)"
            />
            {/* Solid line */}
            <polyline
              points={curve.map(p => { const xy = ternaryToXY(p.w1, p.w2); return `${xy.x},${xy.y}`; }).join(" ")}
              fill="none" stroke={col} strokeWidth="2" opacity="1"
            />
            {/* Invisible thick hit-line for easy clicking */}
            <polyline
              points={curve.map(p => { const xy = ternaryToXY(p.w1, p.w2); return `${xy.x},${xy.y}`; }).join(" ")}
              fill="none" stroke="transparent" strokeWidth="14"
              style={{ cursor: "crosshair", pointerEvents: "stroke" }}
              onClick={(e) => {
                // find nearest curve sample to click
                const svgEl = e.currentTarget.ownerSVGElement;
                const pt = svgEl.createSVGPoint();
                pt.x = e.clientX; pt.y = e.clientY;
                const m = svgEl.getScreenCTM().inverse();
                const local = pt.matrixTransform(m);
                let best = null, bestD = Infinity;
                curve.forEach((p, i) => {
                  const xy = ternaryToXY(p.w1, p.w2);
                  const d = Math.hypot(xy.x - local.x, xy.y - local.y);
                  if (d < bestD) { bestD = d; best = { ...p, idx: i }; }
                });
                if (best && onSelectCurvePoint) onSelectCurvePoint({ ...best, phaseLabel, color: col, segIdx: ci });
              }}
            />
            {/* Sample dots on curve (clickable) */}
            {curve.filter((_, i) => i % 4 === 0).map((p, i) => {
              const xy = ternaryToXY(p.w1, p.w2);
              return (
                <circle key={`s-${i}`} cx={xy.x} cy={xy.y} r="3.5"
                  fill="#ffffff" stroke={col} strokeWidth="1.8"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelectCurvePoint && onSelectCurvePoint({ ...p, phaseLabel, color: col, segIdx: ci })}
                />
              );
            })}
            {/* Curve label (attached to mid-point of curve) */}
            {(() => {
              const midIdx = Math.floor(curve.length * (ci === 0 ? 0.55 : ci === 1 ? 0.6 : 0.5));
              const mid = curve[midIdx];
              const next = curve[Math.min(midIdx + 2, curve.length - 1)];
              const a = ternaryToXY(mid.w1, mid.w2);
              const b = ternaryToXY(next.w1, next.w2);
              // perpendicular offset for label position
              const dx = b.x - a.x, dy = b.y - a.y;
              const len = Math.max(1, Math.hypot(dx, dy));
              const nx = -dy / len, ny = dx / len;  // normal vector
              const offset = ci === 1 ? -22 : 18;     // outward for top curve
              const lx = a.x + nx * offset;
              const ly = a.y + ny * offset;
              const labelText = phaseLabel.length > 14 ? phaseLabel.slice(0, 12) + "…" : phaseLabel;
              const labelW = labelText.length * 6.6 + 14;
              return (
                <g pointerEvents="none">
                  <line x1={a.x} y1={a.y} x2={lx} y2={ly} stroke={col} strokeWidth="1" strokeDasharray="2,2" opacity="0.55" />
                  <rect x={lx - labelW / 2} y={ly - 9} width={labelW} height="18" rx="9"
                        fill="#ffffff" stroke={col} strokeWidth="1.4"
                        filter="drop-shadow(0 2px 4px rgba(15,23,42,0.08))" />
                  <text x={lx} y={ly + 4} textAnchor="middle"
                        fontSize="10" fontWeight="700" fill={col}
                        fontFamily="'IBM Plex Sans', sans-serif">{labelText}</text>
                </g>
              );
            })()}
          </g>
        );
      })}

      {/* Invariant points (LARGER) */}
      {showPrediction && invariantPoints.map((ip, i) => {
        const { x, y } = ternaryToXY(ip.w1, ip.w2);
        return (
          <g key={`inv-${i}`} style={{ cursor: "pointer" }}
             onClick={() => onSelectCurvePoint && onSelectCurvePoint({ w1: ip.w1, w2: ip.w2, ww: 1 - ip.w1 - ip.w2, phaseLabel: `不变点 ${ip.label}`, color: "#0f172a", isInvariant: true })}>
            <circle cx={x} cy={y} r="9" fill="#ffffff" stroke="#0f172a" strokeWidth="2.5" />
            <circle cx={x} cy={y} r="3.5" fill="#0f172a" />
            <text x={x + 14} y={y - 10} fontSize="13" fontWeight="800" fill="#0f172a" fontFamily="'JetBrains Mono', monospace">{ip.label}</text>
          </g>
        );
      })}

      {/* Data points (LARGER, clickable) */}
      {filteredPts.map((pt) => {
        const { x, y } = ternaryToXY(pt.w1, pt.w2);
        const pIdx = phaseList.indexOf(pt.solidPhase);
        const col = PHASE_COLORS[pIdx % PHASE_COLORS.length] || "#64748b";
        const isHovered = hoveredPoint === pt.id;
        const isSelected = selectedPointId === pt.id;
        const r = isSelected ? 11 : isHovered ? 9 : 6.5;
        return (
          <g key={pt.id}
             onMouseEnter={() => onHoverPoint(pt.id)}
             onMouseLeave={() => onHoverPoint(null)}
             onClick={() => onSelectPoint && onSelectPoint(pt)}
             style={{ cursor: "pointer" }}>
            {isSelected && <circle cx={x} cy={y} r={r + 6} fill="none" stroke={col} strokeWidth="2" strokeDasharray="3,3" opacity="0.7"><animate attributeName="r" values={`${r+4};${r+9};${r+4}`} dur="1.8s" repeatCount="indefinite"/></circle>}
            <circle cx={x} cy={y} r={r} fill={col}
                    opacity={isHovered || isSelected ? 1 : 0.88}
                    stroke="#ffffff" strokeWidth="2" />
            {isHovered && !isSelected && (
              <g pointerEvents="none">
                <rect x={x + 14} y={y - 70} width="240" height="86" rx="10" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.2" filter="drop-shadow(0 6px 16px rgba(15,23,42,0.14))" />
                <text x={x + 26} y={y - 50} fontSize="13" fontWeight="700" fill="#0f172a" fontFamily="'JetBrains Mono',monospace">w₁={pt.w1.toFixed(4)}  w₂={pt.w2.toFixed(4)}</text>
                <text x={x + 26} y={y - 33} fontSize="12" fill="#475569" fontFamily="'JetBrains Mono',monospace">w(H₂O)={pt.ww.toFixed(4)}</text>
                <text x={x + 26} y={y - 16} fontSize="12" fontWeight="700" fill={col} fontFamily="'IBM Plex Sans',sans-serif">{pt.solidPhase}</text>
                <text x={x + 26} y={y + 1} fontSize="11" fill="#64748b" fontFamily="'IBM Plex Sans',sans-serif">{pt.source} · σ ±{pt.uncertainty}</text>
                <text x={x + 26} y={y + 14} fontSize="10" fill="#94a3b8" fontFamily="'JetBrains Mono',monospace">点击查看详情 →</text>
              </g>
            )}
          </g>
        );
      })}

      {/* Live prediction marker */}
      {livePoint && (() => {
        const { x, y } = ternaryToXY(livePoint.w1, livePoint.w2);
        return (
          <g>
            <circle cx={x} cy={y} r="18" fill="none" stroke="#0f172a" strokeWidth="1.8" strokeDasharray="4,3">
              <animate attributeName="r" values="18;26;18" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy={y} r="8" fill="#0f172a" stroke="#ffffff" strokeWidth="2.5" />
            <text x={x + 22} y={y + 5} fontSize="13" fontWeight="700" fill="#0f172a" fontFamily="'JetBrains Mono',monospace">查询点</text>
          </g>
        );
      })()}
    </svg>
  );
}
